import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Loader2, CheckCircle2, XCircle, Clock, CalendarDays, ClipboardList } from 'lucide-react';
import { publicChangeOrdersApi } from '../ops/api';
import { unitAbbr } from '../ops/priceBookConstants';
import { DocHeader, DocFooter } from '../components/DocBrand';
import { BUILDBROZ_LOGO_URL } from '../branding';

const money = (c) => (c == null ? '' : (c / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' }));
const signed = (c) => {
  if (c == null) return '';
  const s = money(Math.abs(c));
  return c < 0 ? `-${s}` : `+${s}`;
};
const fmtQty = (q) => {
  const n = Number(q);
  return Number.isNaN(n) ? q : n.toLocaleString('en-US', { maximumFractionDigits: 6 });
};
const fmtDate = (d) => {
  if (!d) return null;
  try { return new Date(`${d}T00:00:00`).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }); } catch { return d; }
};

const Brand = () => (
  <div className="flex items-center gap-3">
    <img src={BUILDBROZ_LOGO_URL} alt="BuildBroz" className="h-11 w-11 object-contain mix-blend-screen" />
    <div>
      <div className="text-white font-brand font-black text-lg tracking-tight">BUILD<span className="text-[#D4AF37]">BROZ</span></div>
      <div className="text-[9px] text-gray-400 uppercase tracking-[0.25em] font-brand">Construction &amp; Remodeling</div>
    </div>
  </div>
);

const Shell = ({ children, showHeader = true }) => (
  <div className="min-h-screen bg-[#0A0A0A]" data-testid="public-co-page">
    {showHeader && (
      <header className="border-b border-gray-800 bg-black">
        <div className="max-w-4xl mx-auto px-4 py-4"><Brand /></div>
      </header>
    )}
    <main className="max-w-4xl mx-auto px-3 sm:px-4 py-6 sm:py-10">{children}</main>
    <footer className="max-w-4xl mx-auto px-4 pb-10 text-center text-xs text-gray-600">
      Questions about this change order? Contact BuildBroz directly.
    </footer>
  </div>
);

const GoldLabel = ({ children }) => (
  <div className="font-brand text-[10px] font-bold uppercase tracking-[0.2em] text-[#D4AF37] mb-1">{children}</div>
);

const MessageCard = ({ title, message, testId }) => (
  <div className="bg-[#141414] border border-gray-800 rounded-lg p-8 text-center" data-testid={testId}>
    <Clock size={32} className="mx-auto text-[#D4AF37] mb-3" />
    <h1 className="text-white text-lg font-semibold mb-2">{title}</h1>
    <p className="text-gray-400 text-sm">{message}</p>
  </div>
);

const inputCls = 'bg-white border-gray-300 text-gray-900 focus:border-[#D4AF37]';

const ApproveDialog = ({ open, onOpenChange, token, coNumber, proposedTotal, onSuccess }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => { if (open) { setName(''); setEmail(''); setAck(false); setError(null); } }, [open]);

  const submit = async () => {
    if (!name.trim()) return setError('Please enter your name.');
    if (!ack) return setError('Please check the acknowledgement box to continue.');
    setBusy(true); setError(null);
    try {
      const r = await publicChangeOrdersApi.approve(token, { name: name.trim(), email: email.trim() || null, acknowledged: true });
      onOpenChange(false);
      onSuccess(r.data);
    } catch (e) {
      const d = e.response?.data?.detail;
      setError(typeof d === 'string' ? d : d?.message || 'Something went wrong. Please try again.');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent aria-describedby={undefined} className="bg-white text-gray-900 max-w-md" data-testid="co-approve-dialog">
        <DialogHeader><DialogTitle className="text-gray-900">Approve Change Order {coNumber}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-gray-600 mb-1 block">Your Name *</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} data-testid="co-approve-name-input" />
          </div>
          <div>
            <label className="text-xs text-gray-600 mb-1 block">Your Email (optional)</label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" className={inputCls} data-testid="co-approve-email-input" />
          </div>
          <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-1" data-testid="co-approve-ack-checkbox" />
            <span>I have reviewed this change order and approve the changes{proposedTotal != null && <> and the new contract total of <b>{money(proposedTotal)}</b></>}.</span>
          </label>
          {error && <p className="text-sm text-red-600" data-testid="co-approve-error">{error}</p>}
          <div className="flex gap-2 justify-end pt-1">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} className="border-gray-300 text-gray-700" data-testid="co-approve-cancel-btn">Cancel</Button>
            <Button onClick={submit} disabled={busy} className="bg-green-600 hover:bg-green-500 text-white font-semibold" data-testid="co-approve-confirm-btn">
              {busy && <Loader2 size={14} className="animate-spin" />} Approve Change Order
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const DeclineDialog = ({ open, onOpenChange, token, coNumber, onSuccess }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => { if (open) { setName(''); setEmail(''); setReason(''); setError(null); } }, [open]);

  const submit = async () => {
    if (!name.trim()) return setError('Please enter your name.');
    setBusy(true); setError(null);
    try {
      const r = await publicChangeOrdersApi.decline(token, { name: name.trim(), email: email.trim() || null, reason: reason.trim() || null });
      onOpenChange(false);
      onSuccess(r.data);
    } catch (e) {
      const d = e.response?.data?.detail;
      setError(typeof d === 'string' ? d : d?.message || 'Something went wrong. Please try again.');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent aria-describedby={undefined} className="bg-white text-gray-900 max-w-md" data-testid="co-decline-dialog">
        <DialogHeader><DialogTitle className="text-gray-900">Decline Change Order {coNumber}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-gray-600 mb-1 block">Your Name *</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} data-testid="co-decline-name-input" />
          </div>
          <div>
            <label className="text-xs text-gray-600 mb-1 block">Your Email (optional)</label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" className={inputCls} data-testid="co-decline-email-input" />
          </div>
          <div>
            <label className="text-xs text-gray-600 mb-1 block">Reason (optional)</label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} className={inputCls} data-testid="co-decline-reason-input" />
          </div>
          {error && <p className="text-sm text-red-600" data-testid="co-decline-error">{error}</p>}
          <div className="flex gap-2 justify-end pt-1">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} className="border-gray-300 text-gray-700" data-testid="co-decline-cancel-btn">Go Back</Button>
            <Button onClick={submit} disabled={busy} className="bg-red-600 hover:bg-red-500 text-white font-semibold" data-testid="co-decline-confirm-btn">
              {busy && <Loader2 size={14} className="animate-spin" />} Decline Change Order
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function PublicChangeOrder() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, notFound: false, data: null });
  const [approveOpen, setApproveOpen] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [responded, setResponded] = useState(null);
  const viewedRef = useRef(false);

  useEffect(() => {
    publicChangeOrdersApi.get(token)
      .then((r) => {
        setState({ loading: false, notFound: false, data: r.data });
        if (r.data.available && !viewedRef.current) {
          viewedRef.current = true;
          publicChangeOrdersApi.viewed(token).catch(() => {});
        }
      })
      .catch(() => setState({ loading: false, notFound: true, data: null }));
  }, [token]);

  if (state.loading) return <Shell><div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#D4AF37]" size={30} /></div></Shell>;
  if (state.notFound) return <Shell><MessageCard title="Change Order Not Found" message="This link is not valid. Please contact BuildBroz if you believe this is an error." testId="public-co-not-found" /></Shell>;
  if (!state.data.available) return <Shell><MessageCard title="Change Order Unavailable" message={state.data.message} testId="public-co-unavailable" /></Shell>;

  const co = state.data.change_order;

  if (responded?.status === 'approved') {
    return (
      <Shell>
        <div className="bg-white rounded-lg p-8 text-center shadow-xl" data-testid="co-approve-success">
          <CheckCircle2 size={48} className="mx-auto text-green-600 mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Change Order Approved</h1>
          <p className="text-gray-600 mb-6">Thank you. Your change order has been approved.</p>
          <div className="inline-block text-left text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded p-4">
            <p><span className="text-gray-500">Change Order:</span> <b>{responded.change_order_number}</b></p>
            <p><span className="text-gray-500">Approved:</span> {new Date(responded.approved_at).toLocaleDateString()}</p>
            <p><span className="text-gray-500">Approved by:</span> {responded.approved_by_customer_name}</p>
            {responded.new_current_contract_value_cents != null && (
              <p><span className="text-gray-500">New contract total:</span> <b data-testid="co-success-new-total">{money(responded.new_current_contract_value_cents)}</b></p>
            )}
          </div>
        </div>
      </Shell>
    );
  }
  if (responded?.status === 'declined') {
    return (
      <Shell>
        <div className="bg-white rounded-lg p-8 text-center shadow-xl" data-testid="co-decline-success">
          <XCircle size={48} className="mx-auto text-gray-500 mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Response Recorded</h1>
          <p className="text-gray-600 mb-6">Your response has been recorded. No changes will be made to your project scope.</p>
          <div className="inline-block text-left text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded p-4">
            <p><span className="text-gray-500">Change Order:</span> <b>{responded.change_order_number}</b></p>
            <p><span className="text-gray-500">Response date:</span> {new Date(responded.declined_at).toLocaleDateString()}</p>
          </div>
        </div>
      </Shell>
    );
  }

  const address = [co.address_line1, co.address_line2, [co.city, co.state].filter(Boolean).join(', '), co.zip_code].filter(Boolean).join(', ');

  const rows = [];
  let lastSection = null, lastGroup = null, lineNo = 0;
  co.lines.forEach((l, i) => {
    if ((l.section_name || null) !== lastSection) {
      lastSection = l.section_name || null;
      lastGroup = null;
      if (lastSection) rows.push({ type: 'section', key: `s-${i}`, label: lastSection });
    }
    if (l.assembly_group_name && l.assembly_group_name !== lastGroup) {
      lastGroup = l.assembly_group_name;
      rows.push({ type: 'group', key: `g-${i}`, label: l.assembly_group_name });
    } else if (!l.assembly_group_name) {
      lastGroup = null;
    }
    lineNo += 1;
    rows.push({ type: 'line', key: `l-${i}`, line: l, no: String(lineNo).padStart(2, '0') });
  });

  return (
    <Shell showHeader={false}>
      {co.status === 'approved' && (
        <div className="mb-4 bg-green-600 text-white rounded-lg px-4 py-3 flex items-center gap-2 font-semibold" data-testid="public-co-approved-banner">
          <CheckCircle2 size={18} /> Approved{co.approved_by_customer_name && <span className="font-normal">— by {co.approved_by_customer_name} on {new Date(co.approved_at).toLocaleDateString()}</span>}
        </div>
      )}
      {co.status === 'declined' && (
        <div className="mb-4 bg-red-600 text-white rounded-lg px-4 py-3 flex items-center gap-2 font-semibold" data-testid="public-co-declined-banner">
          <XCircle size={18} /> Declined
        </div>
      )}
      {co.is_expired && (
        <div className="mb-4 bg-amber-500 text-black rounded-lg px-4 py-3 text-sm font-medium" data-testid="public-co-expired-banner">
          {co.expired_message}
        </div>
      )}

      <div className="bg-[#FAFAF8] rounded-xl shadow-2xl overflow-hidden border border-[#D4AF37]/30">
        <DocHeader />

        <div className="px-5 sm:px-8 py-7">
          {/* Info grid */}
          <div className="grid grid-cols-1 md:grid-cols-[minmax(260px,340px)_1fr] gap-6 items-start">
            <div className="bg-black rounded-lg p-5 border-l-4 border-[#D4AF37]">
              <GoldLabel>Change Order</GoldLabel>
              <div className="text-white font-brand font-extrabold text-xl tracking-tight" data-testid="public-co-number">{co.change_order_number}</div>
              <div className="h-[2px] bg-gradient-to-r from-[#D4AF37] to-transparent my-4" />
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <CalendarDays size={18} className="text-[#D4AF37] shrink-0" />
                  <div>
                    <div className="font-brand text-[9px] font-bold uppercase tracking-[0.18em] text-gray-400">Date</div>
                    <div className="text-white text-sm font-semibold">{fmtDate(co.change_order_date)}</div>
                  </div>
                </div>
                {co.valid_until && (
                  <div className="flex items-center gap-3">
                    <CalendarDays size={18} className="text-[#D4AF37] shrink-0" />
                    <div>
                      <div className="font-brand text-[9px] font-bold uppercase tracking-[0.18em] text-gray-400">Valid Until</div>
                      <div className="text-white text-sm font-semibold">{fmtDate(co.valid_until)}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="md:pt-1">
              <h1 className="font-brand text-2xl sm:text-3xl font-black text-gray-900 tracking-tight" data-testid="public-co-title">{co.title}</h1>
              <div className="mt-4">
                <GoldLabel>Prepared For</GoldLabel>
                <div className="text-gray-900 font-semibold" data-testid="public-co-customer-name">{co.customer_name}</div>
              </div>
              {address && (
                <div className="mt-4">
                  <GoldLabel>Job Location</GoldLabel>
                  <div className="text-gray-800" data-testid="public-co-job-location">{address}</div>
                </div>
              )}
            </div>
          </div>

          {co.reason_for_change && (
            <div className="mt-8">
              <h2 className="font-brand text-sm font-extrabold uppercase tracking-wider text-gray-900 border-b-2 border-[#D4AF37] pb-2 mb-3">Reason for Change</h2>
              <p className="text-sm text-gray-700 whitespace-pre-wrap" data-testid="public-co-reason">{co.reason_for_change}</p>
            </div>
          )}

          <div className="mt-10">
            <div className="flex items-center gap-3 mb-4">
              <ClipboardList size={22} className="text-[#D4AF37] shrink-0" />
              <h2 className="font-brand text-lg sm:text-xl font-black uppercase tracking-wide text-gray-900">Changes to Scope of Work</h2>
              <div className="flex-1 h-[2px] bg-gradient-to-r from-[#D4AF37] to-[#D4AF37]/20 rounded" />
            </div>
            {co.scope_summary && <p className="text-sm text-gray-700 whitespace-pre-wrap mb-5" data-testid="public-co-scope-summary">{co.scope_summary}</p>}

            <div className="rounded-lg overflow-hidden border border-gray-200" data-testid="public-co-lines">
              <div className="grid grid-cols-[2.5rem_1fr_6.5rem] sm:grid-cols-[3.5rem_1fr_12rem_8rem] bg-black px-3 sm:px-4 py-3">
                <span className="font-brand text-[10px] font-bold uppercase tracking-[0.15em] text-[#D4AF37]">#</span>
                <span className="font-brand text-[10px] font-bold uppercase tracking-[0.15em] text-[#D4AF37]">Description</span>
                <span className="hidden sm:block font-brand text-[10px] font-bold uppercase tracking-[0.15em] text-[#D4AF37]">Details</span>
                <span className="font-brand text-[10px] font-bold uppercase tracking-[0.15em] text-[#D4AF37] text-right">Change</span>
              </div>
              <div className="bg-white">
                {rows.map((r) => {
                  if (r.type === 'section') return <div key={r.key} className="font-brand text-xs font-extrabold text-gray-900 uppercase tracking-wider bg-[#D4AF37]/10 border-y border-[#D4AF37]/25 px-4 py-2">{r.label}</div>;
                  if (r.type === 'group') return <div key={r.key} className="font-brand text-[11px] font-bold text-[#8a6d1f] uppercase tracking-wider px-4 pt-3">{r.label}</div>;
                  const l = r.line;
                  const isDeduction = l.direction === 'deduction';
                  return (
                    <div key={r.key} className="grid grid-cols-[2.5rem_1fr_6.5rem] sm:grid-cols-[3.5rem_1fr_12rem_8rem] items-start px-3 sm:px-4 py-3.5 border-b border-[#D4AF37]/15 last:border-b-0">
                      <span className="text-xs font-bold text-gray-400 pt-0.5">{r.no}</span>
                      <div className="min-w-0 pr-3">
                        <div className="text-sm font-bold text-gray-900">
                          <span className={`mr-2 text-[10px] font-semibold px-1.5 py-0.5 rounded ${isDeduction ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>{isDeduction ? '− Deduction' : '+ Addition'}</span>
                          {l.name}
                        </div>
                        {l.description && <p className="text-xs text-gray-500 mt-0.5">{l.description}</p>}
                        <div className="sm:hidden text-xs text-gray-500 mt-1">{fmtQty(l.quantity_decimal)} {unitAbbr(l)} × {money(l.customer_unit_price_cents)}</div>
                      </div>
                      <span className="hidden sm:block text-sm text-gray-600 pt-0.5">{fmtQty(l.quantity_decimal)} {unitAbbr(l)} × {money(l.customer_unit_price_cents)}</span>
                      <span className={`text-sm font-bold text-right ${isDeduction ? 'text-red-700' : 'text-gray-900'}`}>{signed(l.signed_customer_line_impact_cents)}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8 items-stretch">
              <div className="bg-white border border-gray-200 rounded-lg px-5 py-4 space-y-2 text-sm">
                <div className="font-brand text-[10px] font-bold uppercase tracking-[0.2em] text-[#8a6d1f] mb-2">Summary of Changes</div>
                <div className="flex justify-between"><span className="text-gray-600">Additions</span><span className="font-bold text-green-700" data-testid="public-co-additions">{co.addition_total_cents ? `+${money(co.addition_total_cents)}` : money(0)}</span></div>
                <div className="flex justify-between"><span className="text-gray-600">Deductions</span><span className="font-bold text-red-700" data-testid="public-co-deductions">{co.deduction_total_cents ? `-${money(co.deduction_total_cents)}` : money(0)}</span></div>
                <div className="flex justify-between border-t border-[#D4AF37]/30 pt-2 font-bold"><span className="text-gray-800">Net Change</span><span className={(co.net_change_cents || 0) < 0 ? 'text-red-700' : 'text-gray-900'} data-testid="public-co-net-change">{signed(co.net_change_cents)}</span></div>
              </div>
              <div className="bg-black rounded-lg px-6 py-5 border-l-4 border-[#D4AF37] flex flex-col justify-center">
                {co.contract_value_before_cents != null && (
                  <div className="flex justify-between text-sm mb-2"><span className="text-gray-400">Current Contract Value</span><span className="text-white font-semibold" data-testid="public-co-contract-before">{money(co.contract_value_before_cents)}</span></div>
                )}
                <GoldLabel>New Contract Value if Approved</GoldLabel>
                <div className="font-brand font-black text-3xl text-[#D4AF37] tracking-tight" data-testid="public-co-proposed-total">{co.proposed_new_contract_value_cents != null ? money(co.proposed_new_contract_value_cents) : '—'}</div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mt-10">
            <div>
              <h2 className="font-brand text-sm font-extrabold uppercase tracking-wider text-gray-900 border-b-2 border-[#D4AF37] pb-2 mb-3">Exclusions</h2>
              <div className="flex items-start gap-2 text-sm text-gray-700">
                <CheckCircle2 size={16} className="text-[#D4AF37] mt-0.5 shrink-0" />
                <p className="whitespace-pre-wrap" data-testid="public-co-exclusions">{co.exclusions || 'No exclusions'}</p>
              </div>
            </div>
            <div>
              <h2 className="font-brand text-sm font-extrabold uppercase tracking-wider text-gray-900 border-b-2 border-[#D4AF37] pb-2 mb-3">Terms</h2>
              <div className="flex items-start gap-2 text-sm text-gray-700">
                <CheckCircle2 size={16} className="text-[#D4AF37] mt-0.5 shrink-0" />
                <p className="whitespace-pre-wrap" data-testid="public-co-terms">{co.terms || 'No terms'}</p>
              </div>
            </div>
          </div>

          {co.can_respond && (
            <div className="mt-10 border-2 border-[#D4AF37] rounded-xl p-5 sm:p-6 flex flex-col lg:flex-row flex-wrap items-center gap-5 lg:gap-6 bg-white" data-testid="public-co-response-panel">
              <div className="text-center lg:text-left flex-1 min-w-[180px]">
                <h2 className="font-brand text-gray-900 font-black uppercase tracking-wider text-base whitespace-nowrap">Ready to Respond?</h2>
                <p className="text-xs text-gray-500 mt-1">Review the changes and totals above, then choose your response.</p>
              </div>
              <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-3">
                <Button onClick={() => setApproveOpen(true)} className="bg-green-600 hover:bg-green-500 text-white font-brand font-extrabold uppercase tracking-wide px-5 py-6 text-sm rounded-lg" data-testid="public-co-approve-btn">
                  <CheckCircle2 size={18} /> Approve Change Order
                </Button>
                <Button onClick={() => setDeclineOpen(true)} className="bg-red-600 hover:bg-red-500 text-white font-brand font-extrabold uppercase tracking-wide px-5 py-6 text-sm rounded-lg" data-testid="public-co-decline-btn">
                  <XCircle size={18} /> Decline Change Order
                </Button>
              </div>
            </div>
          )}
        </div>

        <DocFooter />
      </div>

      <ApproveDialog open={approveOpen} onOpenChange={setApproveOpen} token={token} coNumber={co.change_order_number} proposedTotal={co.proposed_new_contract_value_cents} onSuccess={setResponded} />
      <DeclineDialog open={declineOpen} onOpenChange={setDeclineOpen} token={token} coNumber={co.change_order_number} onSuccess={setResponded} />
    </Shell>
  );
}
